import axios from "axios";
import { v4 as uuid } from "uuid";
import config from "../config/config";
import { API_BASE_URL, getApiConfig, isDesktopMode } from "./policyService";
import { trackDeleteReference } from "../utils/pendoTracking";

// Global axios config
axios.defaults.withCredentials = true;

// const API_BASE_URL = config.apiBaseUrl;

/**
 * Checks if the current environment is WOPI (true Office Online).
 * Returns true only for WOPI, false for Desktop/SharePoint.
 * @returns {boolean}
 */
export const isWopiEnvironment = () => {
  // isDesktopMode() returns true for Desktop/SharePoint (OIDC mode)
  // So WOPI is when isDesktopMode() returns false
  return !isDesktopMode();
};

/**
 * Storage key for the current policy GUID
 */
const CURRENT_POLICY_GUID_KEY = "currentPolicyGuid";

/**
 * Extracts the policy GUID from a URL.
 * Expected URL formats:
 * - /home/policydoc/edit/{policyGuid}
 * - /home/policydoc/{policyGuid}
 * - /policydoc/edit/{policyGuid}
 * @param {string} url - The URL to extract from
 * @returns {string|null} - The policy GUID or null if not found
 */
export const extractPolicyGuidFromUrl = (url) => {
  if (!url || typeof url !== 'string') return null;
  
  try {
    // Match pattern: /home/policydoc/edit/{guid} or /policydoc/edit/{guid}
    const editMatch = url.match(/\/(?:home\/)?policydoc\/edit\/([a-f0-9-]+)/i);
    if (editMatch && editMatch[1]) {
      console.log("[LEGAL-REF-SERVICE] Extracted policy GUID from edit URL:", editMatch[1]);
      return editMatch[1];
    }
    
    // Match pattern: /home/policydoc/{guid} or /policydoc/{guid}
    const viewMatch = url.match(/\/(?:home\/)?policydoc\/([a-f0-9-]+)/i);
    if (viewMatch && viewMatch[1] && viewMatch[1] !== 'edit') {
      console.log("[LEGAL-REF-SERVICE] Extracted policy GUID from view URL:", viewMatch[1]);
      return viewMatch[1];
    }
    
    return null;
  } catch (error) {
    console.error("[LEGAL-REF-SERVICE] Error extracting policy GUID from URL:", error);
    return null;
  }
};

/**
 * Gets the policy GUID from the current document URL (Office.js).
 * @returns {string|null} - The policy GUID or null if not found
 */
export const getPolicyGuidFromDocumentUrl = () => {
  try {
    if (typeof Office !== 'undefined' && Office.context && Office.context.document && Office.context.document.url) {
      const docUrl = Office.context.document.url;
      console.log("[LEGAL-REF-SERVICE] Document URL:", docUrl);
      const policyGuid = extractPolicyGuidFromUrl(docUrl);
      if (policyGuid) {
        return policyGuid;
      }
    }
    return null;
  } catch (error) {
    console.error("[LEGAL-REF-SERVICE] Error getting policy GUID from document URL:", error);
    return null;
  }
};

/**
 * Gets the policy GUID from WOPI ancestor origins URL.
 * In WOPI environment, the policy edit page URL is in ancestorOrigins[1]:
 * Example: https://april-custom-rds-new.community.diligentoneplatform-dev.com/home/policydoc/edit/2c34dc5a-5212-4542-afe4-7d3ac2251d91
 * The policy GUID is the last part: 2c34dc5a-5212-4542-afe4-7d3ac2251d91
 * @returns {string|null} - The policy GUID or null if not found
 */
export const getPolicyGuidFromWopiUrl = () => {
  try {
    // Only works in WOPI environment
    if (!isWopiEnvironment()) {
      console.log("[LEGAL-REF-SERVICE] Not in WOPI environment, skipping WOPI URL extraction");
      return null;
    }
    
    // In WOPI, the policy editor URL is typically in ancestorOrigins[1]
    // Format: https://domain/home/policydoc/edit/{policyGuid}
    if (window.location.ancestorOrigins && window.location.ancestorOrigins.length > 1) {
      const wopiParentUrl = window.location.ancestorOrigins[1];
      console.log("[LEGAL-REF-SERVICE] WOPI parent URL (ancestorOrigins[1]):", wopiParentUrl);
      
      // Extract policy GUID from URL pattern /home/policydoc/edit/{guid}
      const policyGuid = extractPolicyGuidFromUrl(wopiParentUrl);
      if (policyGuid) {
        console.log("[LEGAL-REF-SERVICE] ✅ Found policy GUID from WOPI URL:", policyGuid);
        return policyGuid;
      }
    }
    
    // Fallback: check ancestorOrigins[0] as well
    if (window.location.ancestorOrigins && window.location.ancestorOrigins.length > 0) {
      const firstAncestor = window.location.ancestorOrigins[0];
      console.log("[LEGAL-REF-SERVICE] Checking first ancestor origin:", firstAncestor);
      const policyGuid = extractPolicyGuidFromUrl(firstAncestor);
      if (policyGuid) {
        console.log("[LEGAL-REF-SERVICE] ✅ Found policy GUID from first ancestor:", policyGuid);
        return policyGuid;
      }
    }
    
    console.log("[LEGAL-REF-SERVICE] No policy GUID found in WOPI ancestor origins");
    return null;
  } catch (error) {
    console.error("[LEGAL-REF-SERVICE] Error getting policy GUID from WOPI URL:", error);
    return null;
  }
};

/**
 * Module-level cache for policy GUID read from document custom properties.
 * This avoids repeated Word.run() calls for every API request.
 */
let _cachedDocPolicyGuid = null;
let _docPolicyGuidInitialized = false;

/**
 * Gets the policy GUID from document custom properties.
 * The backend injects 'PolicyGuid' as a custom document property in the WOPI GetFile response.
 * Results are cached in memory (per taskpane instance) for subsequent calls.
 * @param {boolean} forceRefresh - Force re-reading from document (bypass cache)
 * @returns {Promise<string|null>} - The policy GUID or null if not found
 */
export const getPolicyGuidFromDocumentProperties = async (forceRefresh = false) => {
  try {
    // Return cached value if already successfully read
    if (_docPolicyGuidInitialized && _cachedDocPolicyGuid && !forceRefresh) {
      console.log("[LEGAL-REF-SERVICE] ✅ Using cached policy GUID from document properties:", _cachedDocPolicyGuid);
      return _cachedDocPolicyGuid;
    }

    if (typeof Word === 'undefined' || typeof Word.run !== 'function') {
      console.log("[LEGAL-REF-SERVICE] Word.run not available, skipping document properties check");
      return null;
    }
    
    const policyGuid = await Word.run(async (context) => {
      const customDocProps = context.document.properties.customProperties;
      context.load(customDocProps, 'items');
      await context.sync();
      
      // Look for 'PolicyGuid' property (injected by backend in WopiExtensions.GetFile)
      const prop = customDocProps.items.find(p => p.key === 'PolicyGuid');
      if (prop && prop.value) {
        console.log("[LEGAL-REF-SERVICE] ✅ Found PolicyGuid in document custom properties:", prop.value);
        return prop.value;
      }
      
      console.log("[LEGAL-REF-SERVICE] No PolicyGuid found in document custom properties");
      return null;
    });

    if (policyGuid) {
      // Cache the result in memory (not localStorage!) for this taskpane instance
      _cachedDocPolicyGuid = policyGuid;
      _docPolicyGuidInitialized = true;
    }

    return policyGuid;
  } catch (error) {
    console.error("[LEGAL-REF-SERVICE] Error getting policy GUID from document properties:", error);
    return null;
  }
};

/**
 * Gets the current policy GUID from WOPI environment.
 * ONLY works in WOPI environment - returns null for Desktop/SharePoint.
 * 
 * PRIMARY SOURCE: The policyGuid is read from the 'policyguid' cookie via the dialogue
 * and stored in sessionStorage. Using sessionStorage ensures each document instance
 * (taskpane iframe) has its own isolated policy GUID, allowing multiple policies to be
 * opened in parallel without conflicts.
 * 
 * FALLBACK: sessionStorage (from dialog cookie - tab-scoped, better isolation than localStorage)
 * 
 * NOTE: This function is async because reading document custom properties requires Word.run().
 * All callers (addPolicyRelationship, removePolicyRelationship, etc.) are already async.
 * 
 * @returns {Promise<string|null>} - The policy GUID or null if not found or not in WOPI
 */
export const getCurrentPolicyGuid = async () => {
  try {
    console.log("[LEGAL-REF-SERVICE] 🔍 Looking for current policy GUID...");
    
    // CRITICAL: Only track policy relationships in WOPI environment
    if (!isWopiEnvironment()) {
      console.log("[LEGAL-REF-SERVICE] ⏭️ Not in WOPI environment (Desktop/SharePoint) - skipping policy tracking");
      return null;
    }
    
    console.log("[LEGAL-REF-SERVICE] ✅ WOPI environment detected - proceeding with policy GUID lookup");
    
    // PRIMARY: Read from document custom properties (injected by backend via WOPI GetFile)
    // This is the most reliable source as it's document-specific and persistent
    const docPropGuid = await getPolicyGuidFromDocumentProperties();
    if (docPropGuid) {
      console.log("[LEGAL-REF-SERVICE] ✅ Current policy GUID from document properties (primary):", docPropGuid);
      return docPropGuid;
    }
    
    // FALLBACK 1: sessionStorage (set via dialogue from cookie - tab-scoped)
    const policyGuid = sessionStorage.getItem(CURRENT_POLICY_GUID_KEY);
    if (policyGuid) {
      console.warn("[LEGAL-REF-SERVICE] ⚠️ Using sessionStorage policyGuid as fallback (tab-specific):", policyGuid);
      return policyGuid;
    }
    
    // FALLBACK 2: Try to get from WOPI ancestor origins URL (may not work if URL is incomplete)
    const wopiPolicyGuid = getPolicyGuidFromWopiUrl();
    if (wopiPolicyGuid) {
      console.warn("[LEGAL-REF-SERVICE] ⚠️ Current policy GUID from WOPI URL (fallback):", wopiPolicyGuid);
      sessionStorage.setItem(CURRENT_POLICY_GUID_KEY, wopiPolicyGuid);
      return wopiPolicyGuid;
    }
    
    console.error("[LEGAL-REF-SERVICE] ⚠️ WOPI environment but no policy GUID found in document properties, sessionStorage, or WOPI URL.");
    console.error("[LEGAL-REF-SERVICE] Ensure backend injects PolicyGuid in document custom properties via WOPI GetFile");
    return null;
  } catch (error) {
    console.error("[LEGAL-REF-SERVICE] ❌ Error getting current policy GUID:", error);
    return null;
  }
};

/**
 * Gets the current policy GUID from WOPI environment (async version).
 * Since getCurrentPolicyGuid() is now async, this is a simple wrapper for backward compatibility.
 * @returns {Promise<string|null>} - The policy GUID or null if not found or not in WOPI
 */
export const getCurrentPolicyGuidAsync = async () => {
  return await getCurrentPolicyGuid();
};

/**
 * Sets the current policy GUID in storage.
 * This should be called when a document is opened from the policy editor.
 * Uses sessionStorage to ensure each document instance has its own isolated policy GUID,
 * allowing multiple policies to be opened in parallel without conflicts.
 * @param {string} policyGuid - The policy GUID to store
 */
export const setCurrentPolicyGuid = (policyGuid) => {
  try {
    if (policyGuid) {
      sessionStorage.setItem(CURRENT_POLICY_GUID_KEY, policyGuid);
      console.log("[LEGAL-REF-SERVICE] Current policy GUID stored in sessionStorage:", policyGuid);
    } else {
      sessionStorage.removeItem(CURRENT_POLICY_GUID_KEY);
      console.log("[LEGAL-REF-SERVICE] Current policy GUID cleared from sessionStorage");
    }
  } catch (error) {
    console.error("[LEGAL-REF-SERVICE] Error setting current policy GUID:", error);
  }
};

/**
 * Clears the current policy GUID from storage.
 * Clears from both sessionStorage (primary) and localStorage (for cleanup of old data).
 */
export const clearCurrentPolicyGuid = () => {
  try {
    localStorage.removeItem(CURRENT_POLICY_GUID_KEY);
    sessionStorage.removeItem(CURRENT_POLICY_GUID_KEY);
    // Also clear from localStorage for cleanup (in case old data exists)
    localStorage.removeItem(CURRENT_POLICY_GUID_KEY);
    console.log("[LEGAL-REF-SERVICE] Current policy GUID cleared from sessionStorage and localStorage");
  } catch (error) {
    console.error("[LEGAL-REF-SERVICE] Error clearing current policy GUID:", error);
  }
};

/**
 * Fetches the list of legal references from the backend.
 * @returns {Promise<Object[]>} List of legal references.
 */
export const getLegalReferenceList = async () => {
  try {
    const apiConfig = await getApiConfig();
    const response = await axios.get(`${apiConfig.baseUrl}/api/LegalReferences?settings=false`, {
      headers: apiConfig.headers,
      ...(isDesktopMode() ? {} : { withCredentials: true })
    });
    return response.data;
  } catch (error) {
    console.error("Error fetching legal references:", error);
    throw error;
  }
};

/**
 * Fetches a paginated list of legal references from the backend.
 * @param {number} page - Page number (1-based).
 * @param {number} pageSize - Number of items per page.
 * @param {string} [search] - Optional search query to filter results.
 * @returns {Promise<{items: Object[], hasMore: boolean}>} Object with items array and hasMore flag.
 */
export const getLegalReferenceListPaged = async (page = 1, pageSize = 20, search = "") => {
  try {
    const apiConfig = await getApiConfig();
    const searchParam = search && search.trim() ? `&search=${encodeURIComponent(search.trim())}` : "";
    const response = await axios.get(
      `${apiConfig.baseUrl}/api/LegalReferences/ListPaged?page=${page}&pageSize=${pageSize}&showDeletedOnly=false${searchParam}`,
      {
        headers: apiConfig.headers,
        ...(isDesktopMode() ? {} : { withCredentials: true })
      }
    );
    const data = response.data;
    // Support both array response and object with items/data property
    const items = Array.isArray(data) ? data : (data?.items ?? data?.data ?? []);
    const totalCount = data?.totalCount ?? data?.total ?? null;
    const totalPages = data?.totalPages ?? null;
    const hasMore = totalPages != null
      ? page < totalPages
      : totalCount != null
        ? page * pageSize < totalCount
        : items.length >= pageSize;
    return { items, hasMore };
  } catch (error) {
    console.error("Error fetching legal references (paged):", error);
    throw error;
  }
};

/**
 * Adds a new legal reference to the backend.
 * @param {string} name - Name of the legal reference.
 * @param {string} link - URL link of the legal reference.
 * @returns {Promise<Object>} Created legal reference data.
 */
export const addLegalReference = async (name, link) => {
  const guid = uuid();
  const apiConfig = await getApiConfig();
  const url = `${apiConfig.baseUrl}/api/LegalReference/${guid}/create?settings=false`;

  const payload = { name, link, guid };

  try {
    const response = await axios.post(url, payload, {
      headers: apiConfig.headers,
      ...(isDesktopMode() ? {} : { withCredentials: true })
    });
    return response.data;
  } catch (error) {
    console.error("Error adding legal reference:", error);
    throw error;
  }
};

/**
 * Adds a policy relationship for a legal reference.
 * This tracks that a legal reference is used in a specific policy document.
 * @param {string} legalReferenceGuid - The GUID of the legal reference
 * @param {string} policyGuid - The GUID of the policy (optional, will use current policy if not provided)
 * @returns {Promise} - The API response
 */
export const addPolicyRelationship = async (legalReferenceGuid, policyGuid = null) => {
  console.log(`[LEGAL-REF-SERVICE] 🚀 addPolicyRelationship called with:`, {
    legalReferenceGuid,
    policyGuid
  });
  
  try {
    // Use provided policyGuid or get from current context (document properties → sessionStorage fallback)
    const targetPolicyGuid = policyGuid || await getCurrentPolicyGuid();
    
    console.log(`[LEGAL-REF-SERVICE] Target policy GUID resolved to:`, targetPolicyGuid);
    
    if (!targetPolicyGuid) {
      console.warn("[LEGAL-REF-SERVICE] ⚠️ Cannot add policy relationship - no policy GUID available");
      console.warn("[LEGAL-REF-SERVICE] Ensure backend injects PolicyGuid in document custom properties via WOPI GetFile");
      return { success: false, status: null, data: null, error: "No policy GUID available" };
    }
    
    if (!legalReferenceGuid) {
      console.warn("[LEGAL-REF-SERVICE] ⚠️ Cannot add policy relationship - no legal reference GUID provided");
      return { success: false, status: null, data: null, error: "No legal reference GUID provided" };
    }
    
    const apiConfig = await getApiConfig();
    const url = `${apiConfig.baseUrl}/api/LegalReference/${legalReferenceGuid}/AddPolicyRelationship?policyGuid=${targetPolicyGuid}`;

    console.log(`[LEGAL-REF-SERVICE] 📡 Making API call to:`, url);

    const response = await axios.post(
      url,
      {},
      {
        headers: apiConfig.headers,
        ...(isDesktopMode() ? {} : { withCredentials: true })
      }
    );
    
    console.log(`[LEGAL-REF-SERVICE] ✅ Successfully added policy relationship for legal reference ${legalReferenceGuid}`, response.data);
    return { success: true, status: response.status, data: response.data, error: null };
  } catch (error) {
    const statusCode = error.response?.status || null;
    console.error(`[LEGAL-REF-SERVICE] ❌ Error adding policy relationship for legal reference ${legalReferenceGuid}:`, error);
    console.error(`[LEGAL-REF-SERVICE] Error details: status=${statusCode}`, error.response?.data || error.message);
    
    // Return structured error so callers can decide how to handle
    return { 
      success: false, 
      status: statusCode, 
      data: error.response?.data || null, 
      error: error.message 
    };
  }
};

/**
 * Removes a policy relationship for a legal reference.
 * This tracks that a legal reference is no longer used in a specific policy document.
 * @param {string} legalReferenceGuid - The GUID of the legal reference
 * @param {string} policyGuid - The GUID of the policy (optional, will use current policy if not provided)
 * @returns {Promise} - The API response
 */
export const removePolicyRelationship = async (legalReferenceGuid, policyGuid = null) => {
  console.log(`[LEGAL-REF-SERVICE] 🚀 removePolicyRelationship called with:`, {
    legalReferenceGuid,
    policyGuid
  });
  
  try {
    // Use provided policyGuid or get from current context (document properties → localStorage fallback)
    const targetPolicyGuid = policyGuid || await getCurrentPolicyGuid();
    
    console.log(`[LEGAL-REF-SERVICE] Target policy GUID resolved to:`, targetPolicyGuid);
    
    if (!targetPolicyGuid) {
      console.warn("[LEGAL-REF-SERVICE] ⚠️ Cannot remove policy relationship - no policy GUID available");
      return null;
    }
    
    if (!legalReferenceGuid) {
      console.warn("[LEGAL-REF-SERVICE] ⚠️ Cannot remove policy relationship - no legal reference GUID provided");
      return null;
    }
    
    const apiConfig = await getApiConfig();
    const url = `${apiConfig.baseUrl}/api/LegalReference/${legalReferenceGuid}/RemovePolicyRelationship?policyGuid=${targetPolicyGuid}`;

    console.log(`[LEGAL-REF-SERVICE] 📡 Making DELETE API call to:`, url);

    const response = await axios.post(url, {}, {
      headers: apiConfig.headers,
      ...(isDesktopMode() ? {} : { withCredentials: true })
    });
    
    console.log(`[LEGAL-REF-SERVICE] ✅ Successfully removed policy relationship for legal reference ${legalReferenceGuid}`, response.data);
    return response.data;
  } catch (error) {
    console.error(`[LEGAL-REF-SERVICE] ❌ Error removing policy relationship for legal reference ${legalReferenceGuid}:`, error);
    console.error(`[LEGAL-REF-SERVICE] Error details:`, error.response?.data || error.message);
    // Don't throw - we don't want to block the main flow if tracking fails
    return null;
  }
};

/**
 * Tracks policy relationships for multiple legal references.
 * Calls addPolicyRelationship for each legal reference GUID provided.
 * @param {string[]} legalReferenceGuids - Array of legal reference GUIDs
 * @param {string} policyGuid - The GUID of the policy (optional, will use current policy if not provided)
 * @returns {Promise} - Resolves when all tracking calls are complete
 */
/**
 * Tracks policy relationships for multiple legal references.
 * Calls addPolicyRelationship for each legal reference GUID provided.
 * Returns a structured result indicating success/failure and whether a 400 (unauthorized) occurred.
 * @param {string[]} legalReferenceGuids - Array of legal reference GUIDs
 * @param {string} policyGuid - The GUID of the policy (optional, will use current policy if not provided)
 * @returns {Promise<{success: boolean, hasAuthError: boolean, results: Array}>} - Structured result
 */
export const trackPolicyRelationships = async (legalReferenceGuids, policyGuid = null) => {
  console.log(`[LEGAL-REF-SERVICE] 🚀 trackPolicyRelationships called with:`, {
    legalReferenceGuids,
    policyGuid
  });
  
  // CRITICAL: Only track in WOPI environment
  if (!isWopiEnvironment()) {
    console.log("[LEGAL-REF-SERVICE] ⏭️ Not in WOPI environment (Desktop/SharePoint) - skipping policy tracking");
    return { success: true, hasAuthError: false, results: [] };
  }
  
  if (!legalReferenceGuids || legalReferenceGuids.length === 0) {
    console.warn("[LEGAL-REF-SERVICE] ⚠️ No legal reference GUIDs provided for tracking");
    return { success: true, hasAuthError: false, results: [] };
  }
  
  const targetPolicyGuid = policyGuid || await getCurrentPolicyGuid();
  
  console.log(`[LEGAL-REF-SERVICE] Target policy GUID resolved to:`, targetPolicyGuid);
  
  if (!targetPolicyGuid) {
    console.warn("[LEGAL-REF-SERVICE] ⚠️ Cannot track policy relationships - no policy GUID available");
    console.warn("[LEGAL-REF-SERVICE] Ensure backend injects PolicyGuid in document custom properties via WOPI GetFile");
    return { success: false, hasAuthError: false, results: [] };
  }
  
  console.log(`[LEGAL-REF-SERVICE] 📡 Tracking ${legalReferenceGuids.length} legal reference(s) for policy ${targetPolicyGuid}`);
  
  // Filter out null/undefined values
  const validGuids = legalReferenceGuids.filter(guid => guid);
  console.log(`[LEGAL-REF-SERVICE] Valid GUIDs to track:`, validGuids);
  
  // Call addPolicyRelationship for each legal reference
  const trackingPromises = validGuids.map(legalRefGuid => addPolicyRelationship(legalRefGuid, targetPolicyGuid));
  
  try {
    const results = await Promise.all(trackingPromises);
    console.log("[LEGAL-REF-SERVICE] Tracking results:", results);
    
    // Check if any result has a 400 status (authorization/session error)
    const hasAuthError = results.some(r => r && r.status === 400);
    const allSucceeded = results.every(r => r && r.success);
    
    if (hasAuthError) {
      console.error("[LEGAL-REF-SERVICE] ❌ Authorization error (400) detected in tracking results");
    } else if (allSucceeded) {
      console.log("[LEGAL-REF-SERVICE] ✅ All policy relationships tracked successfully");
    }
    
    return { success: allSucceeded, hasAuthError, results };
  } catch (error) {
    console.error("[LEGAL-REF-SERVICE] ❌ Error tracking some policy relationships:", error);
    return { success: false, hasAuthError: false, results: [] };
  }
};

/**
 * Removes policy relationships for multiple legal references.
 * Calls removePolicyRelationship for each legal reference GUID provided.
 * @param {string[]} legalReferenceGuids - Array of legal reference GUIDs
 * @param {string} policyGuid - The GUID of the policy (optional, will use current policy if not provided)
 * @returns {Promise} - Resolves when all removal calls are complete
 */
export const untrackPolicyRelationships = async (legalReferenceGuids, policyGuid = null) => {
  console.log(`[LEGAL-REF-SERVICE] 🚀 untrackPolicyRelationships called with:`, {
    legalReferenceGuids,
    policyGuid
  });
  
  // CRITICAL: Only untrack in WOPI environment
  if (!isWopiEnvironment()) {
    console.log("[LEGAL-REF-SERVICE] ⏭️ Not in WOPI environment (Desktop/SharePoint) - skipping policy untracking");
    return;
  }
  
  if (!legalReferenceGuids || legalReferenceGuids.length === 0) {
    console.warn("[LEGAL-REF-SERVICE] No legal reference GUIDs provided for untracking");
    return;
  }
  
  const targetPolicyGuid = policyGuid || await getCurrentPolicyGuid();
  
  if (!targetPolicyGuid) {
    console.warn("[LEGAL-REF-SERVICE] Cannot untrack policy relationships - no policy GUID available");
    return;
  }
  
  console.log(`[LEGAL-REF-SERVICE] 📡 Untracking ${legalReferenceGuids.length} legal reference(s) for policy ${targetPolicyGuid}`);
  
  // Call removePolicyRelationship for each legal reference
  const untrackingPromises = legalReferenceGuids
    .filter(guid => guid) // Filter out null/undefined
    .map(legalRefGuid => removePolicyRelationship(legalRefGuid, targetPolicyGuid));
  
  try {
    await Promise.all(untrackingPromises);
    console.log("[LEGAL-REF-SERVICE] ✅ All policy relationships removed successfully");
  } catch (error) {
    console.error("[LEGAL-REF-SERVICE] ❌ Error removing some policy relationships:", error);
    // Don't throw - we don't want to block the main flow
  }
};

/**
 * Storage key for tracking added legal references in the document
 */
const TRACKED_REFERENCES_KEY = "trackedLegalReferences";

/**
 * Gets the list of tracked legal references from storage.
 * These are references that have been added to the current document.
 * @returns {Object[]} - Array of tracked reference objects {guid, id, name, url}
 */
export const getTrackedReferences = () => {
  try {
    const stored = localStorage.getItem(TRACKED_REFERENCES_KEY);
    if (stored) {
      return JSON.parse(stored);
    }
    return [];
  } catch (error) {
    console.error("[LEGAL-REF-SERVICE] Error getting tracked references:", error);
    return [];
  }
};

/**
 * Adds a legal reference to the tracking list.
 * This should be called when a reference is added to the document.
 * @param {Object} reference - Reference object {guid, id, name, url}
 */
export const addTrackedReference = (reference) => {
  try {
    if (!reference || (!reference.guid && !reference.id)) {
      console.warn("[LEGAL-REF-SERVICE] Cannot track reference without guid or id");
      return;
    }
    
    const tracked = getTrackedReferences();
    
    // Check if already tracked
    const existing = tracked.find(r => 
      (r.guid && r.guid === reference.guid) || 
      (r.id && r.id === reference.id)
    );
    
    if (!existing) {
      tracked.push({
        guid: reference.guid,
        id: reference.id,
        name: reference.name || reference.title,
        url: reference.url,
        addedAt: new Date().toISOString()
      });
      localStorage.setItem(TRACKED_REFERENCES_KEY, JSON.stringify(tracked));
      console.log("[LEGAL-REF-SERVICE] ✅ Reference added to tracking:", reference.guid || reference.id);
    }
  } catch (error) {
    console.error("[LEGAL-REF-SERVICE] Error adding tracked reference:", error);
  }
};

/**
 * Adds multiple legal references to the tracking list.
 * @param {Object[]} references - Array of reference objects {guid, id, name, url}
 */
export const addTrackedReferences = (references) => {
  if (!references || references.length === 0) return;
  
  references.forEach(ref => addTrackedReference(ref));
};

/**
 * Removes a legal reference from the tracking list.
 * This should be called when a reference is removed from the document.
 * @param {string} guidOrId - The GUID or ID of the reference to remove
 */
export const removeTrackedReference = (guidOrId) => {
  try {
    if (!guidOrId) return;
    
    const tracked = getTrackedReferences();
    const filtered = tracked.filter(r => 
      r.guid !== guidOrId && r.id !== guidOrId
    );
    
    if (filtered.length !== tracked.length) {
      localStorage.setItem(TRACKED_REFERENCES_KEY, JSON.stringify(filtered));
      console.log("[LEGAL-REF-SERVICE] ✅ Reference removed from tracking:", guidOrId);
    }
  } catch (error) {
    console.error("[LEGAL-REF-SERVICE] Error removing tracked reference:", error);
  }
};

/**
 * Clears all tracked references.
 * This should be called when starting a new document or on logout.
 */
export const clearTrackedReferences = () => {
  try {
    localStorage.removeItem(TRACKED_REFERENCES_KEY);
    console.log("[LEGAL-REF-SERVICE] ✅ All tracked references cleared");
  } catch (error) {
    console.error("[LEGAL-REF-SERVICE] Error clearing tracked references:", error);
  }
};

/**
 * Extracts legal reference ID from a redirect URL.
 * @param {string} url - The URL to extract ID from
 * @returns {string|null} - The extracted ID or null
 */
export const extractLegalRefIdFromUrl = (url) => {
  if (!url || typeof url !== 'string') return null;
  
  // Match pattern: /api/LegalReference/{id}/Redirect
  const match = url.match(/\/api\/LegalReference\/([^\/]+)\/Redirect/i);
  if (match && match[1]) {
    return match[1];
  }
  
  // Also try full URL pattern (with domain)
  const match2 = url.match(/https?:\/\/[^\/]+\/api\/LegalReference\/([^\/]+)\/Redirect/i);
  if (match2 && match2[1]) {
    return match2[1];
  }
  
  return null;
};

/**
 * Checks if a URL is a legal reference URL (vs policy reference).
 * @param {string} url - The URL to check
 * @returns {boolean} - True if it's a legal reference URL
 */
export const isLegalReferenceUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  // Legal references use /api/LegalReference/{id}/Redirect pattern
  // Policy references use /home/policydoc/{guid} pattern
  return /\/api\/LegalReference\/[^\/]+\/Redirect/i.test(url);
};

/**
 * Looks up the GUID for a legal reference by its numeric ID.
 * @param {string} idOrGuid - The ID or GUID to look up
 * @returns {Promise<string|null>} - The GUID or null if not found
 */
export const lookupGuidFromId = async (idOrGuid) => {
  // If it's already a GUID (UUID format), return it
  if (idOrGuid && /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(idOrGuid)) {
    return idOrGuid;
  }
  
  try {
    const legalRefs = await getLegalReferenceList();
    const ref = legalRefs.find(r => String(r.id) === String(idOrGuid));
    if (ref && ref.guid) {
      return ref.guid;
    }
    return null;
  } catch (error) {
    console.error(`[LEGAL-REF-SERVICE] Error looking up GUID for ID ${idOrGuid}:`, error);
    return null;
  }
};

/**
 * Tracks Pendo events for deleted references (separate function to avoid impacting core functionality)
 * This function detects deleted references independently and tracks them in Pendo
 * @param {string[]} currentReferenceIds - Array of legal reference IDs currently in the document
 */
export const trackPendoEventsForDeletedReferences = async (currentReferenceIds = []) => {
  // If currentReferenceIds is null/undefined, scan failed — skip
  if (currentReferenceIds === null || currentReferenceIds === undefined) {
    return;
  }
  
  try {
    const tracked = getTrackedReferences();
    if (tracked.length === 0) {
      return;
    }
    
    // Convert current IDs to a Set for efficient lookup
    const currentSet = new Set(currentReferenceIds.map(id => String(id)));
    
    // Find references that were tracked but are no longer in the document
    const deletedRefs = tracked.filter(ref => {
      const refId = ref.id ? String(ref.id) : ref.guid;
      return !currentSet.has(refId) && !currentSet.has(ref.guid);
    });
    
    if (deletedRefs.length === 0) {
      return;
    }
    
    // Track Pendo events for deleted references (non-blocking, fire-and-forget)
    deletedRefs.forEach(ref => {
      try {
        const refId = ref.id || ref.guid || 'unknown';
        const refName = ref.name || 'unnamed';
        trackDeleteReference({
          refNumber: refId,
          success: true,
          message: `Reference ${refId} (${refName}) deleted from document`,
        });
      } catch (pendoError) {
        // Silently fail - Pendo tracking should not affect core functionality
        console.warn(`[LEGAL-REF-SERVICE] ⚠️ Pendo tracking failed for reference (non-critical):`, pendoError);
      }
    });
  } catch (error) {
    // Silently fail - Pendo tracking should not affect core functionality
    console.warn(`[LEGAL-REF-SERVICE] ⚠️ Pendo tracking failed (non-critical):`, error);
  }
};

/**
 * Detects and handles deleted legal references by comparing tracked references
 * with the current references in the document.
 * @param {string[]} currentReferenceIds - Array of legal reference IDs currently in the document
 * @returns {Promise} - Resolves when deletion handling is complete
 */
export const detectAndHandleDeletedReferences = async (currentReferenceIds = []) => {
  // If currentReferenceIds is null/undefined, scan failed — skip to avoid false deletions
  if (currentReferenceIds === null || currentReferenceIds === undefined) {
    console.warn("[LEGAL-REF-SERVICE] ⚠️ currentReferenceIds is null/undefined (scan failed), skipping deletion detection");
    return;
  }
  
  const policyGuid = await getCurrentPolicyGuid();
  if (!policyGuid) {
    console.log("[LEGAL-REF-SERVICE] No policy GUID available, skipping deletion detection");
    return;
  }
  
  const tracked = getTrackedReferences();
  if (tracked.length === 0) {
    console.log("[LEGAL-REF-SERVICE] No tracked references, skipping deletion detection");
    return;
  }
  
  // NOTE: Scan failures return null (handled above on line 736).
  // An empty array (length === 0) means the scan succeeded but found no references — 
  // the user genuinely deleted all references, so proceed with deletion detection.
  console.log(`[LEGAL-REF-SERVICE] 📊 Scan found ${currentReferenceIds.length} reference(s) in document, ${tracked.length} tracked locally`);
  
  // Convert current IDs to a Set for efficient lookup
  const currentSet = new Set(currentReferenceIds.map(id => String(id)));
  
  // Find references that were tracked but are no longer in the document
  const deletedRefs = tracked.filter(ref => {
    const refId = ref.id ? String(ref.id) : ref.guid;
    return !currentSet.has(refId) && !currentSet.has(ref.guid);
  });
  
  if (deletedRefs.length === 0) {
    return;
  }
  
  console.log(`[LEGAL-REF-SERVICE] Detected ${deletedRefs.length} deleted reference(s)`);
  
  // Handle each deleted reference
  for (const ref of deletedRefs) {
    try {
      let guidToRemove = ref.guid;
      
      // If we only have ID, try to look up the GUID
      if (!guidToRemove && ref.id) {
        guidToRemove = await lookupGuidFromId(ref.id);
      }
      
      if (guidToRemove) {
        await removePolicyRelationship(guidToRemove, policyGuid);
        console.log(`[LEGAL-REF-SERVICE] ✅ Removed policy relationship for deleted reference: ${guidToRemove}`);
      }
      
      // Remove from tracking
      removeTrackedReference(ref.guid || ref.id);
    } catch (error) {
      console.error(`[LEGAL-REF-SERVICE] ❌ Error handling deleted reference:`, error);
    }
  }
};
